import {
  Combobox,
  ComboboxDropdown,
  ComboboxOption,
  ComboboxSelectButton,
  ComboboxSelectCheck,
  Icon,
  Stack,
  Text,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

import { useAppointmentSelectableLocations } from '../useAppointmentSelectableLocations';

interface LocationOption {
  id: string;
  label: string;
}

interface LocationSelectorProps {
  tenantId: string;
  selectedLocationId?: string;
  onLocationSelected?: (location?: LocationOption) => void;
  variant?: 'default' | 'subtle';
  disabled?: boolean;
}

export const LocationSelector: React.FC<LocationSelectorProps> = ({
  tenantId,
  onLocationSelected,
  selectedLocationId,
  variant,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const locations = useAppointmentSelectableLocations(tenantId);

  const selectedLocation = locations.find(
    (location) => location.id === selectedLocationId,
  );

  const handleLocationSelected = (location?: LocationOption) => {
    onLocationSelected?.(location);
  };

  return (
    <Combobox disabled={disabled}>
      <ComboboxSelectButton
        className="w-full overflow-hidden border-transparent hover:bg-bg-quaternary"
        variant={variant}
        title={selectedLocation?.label}
      >
        <Stack className="overflow-hidden" direction="horizontal" gap={2}>
          <Icon name="MapPin" />
          <Text className="flex-1 truncate text-left">
            {selectedLocation?.label ??
              t('smartBlocks.locationSelector.selectLocation')}
          </Text>
        </Stack>
      </ComboboxSelectButton>
      <ComboboxDropdown
        searchPlaceholder={t('smartBlocks.locationSelector.searchPlaceholder')}
      >
        {locations.map((location) => (
          <ComboboxOption
            key={location.id}
            onSelect={() => handleLocationSelected(location)}
          >
            <ComboboxSelectCheck
              className="mr-2"
              selected={selectedLocation?.id === location.id}
            />
            {location.label}
          </ComboboxOption>
        ))}
        <ComboboxOption onSelect={() => handleLocationSelected(undefined)}>
          <ComboboxSelectCheck
            selected={selectedLocation === undefined}
            className="mr-2"
          />
          {t('smartBlocks.locationSelector.noLocation')}
        </ComboboxOption>
      </ComboboxDropdown>
    </Combobox>
  );
};
